import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID } from '@adornis/baseql/baseqlTypes.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerMutation, registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { mongo } from '@adornis/buchungsheld-db/server/mongo.js';
import { context } from '@adornis/baseql/server/context.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { User } from '@adornis/buchungsheld-db/entities/user.js';
import { NotLoggedInError } from '@adornis/users/db/errors.js';
import { from, map, switchMap } from 'rxjs';
import { MatchingWeekPermissions, MatchingWeekRole } from '../db/enums/roles.js';
import { JobApplication } from '../db/job-application.js';
import { JobPosition } from '../db/job-position.js';
import { MWUserData } from '../db/user.js';

type MatchingWeekUser = User & { hasPermission(permission: MatchingWeekPermissions, context?: string): boolean };

export const getAndEnsureUserDataOfUserID = registerQuery({
  operationName: 'getAndEnsureUserDataOfUserID',
  type: () => MWUserData,
  resolve: (userID: string) => async (gqlFields: BaseQLSelectionSet<MWUserData>) => {
    if (!context.serverContext) {
      const caller = (await context.user()) as Maybe<MatchingWeekUser>;
      if (!caller) throw new NotLoggedInError();
      if (caller._id !== userID && !caller.roles?.some(role => role.name === MatchingWeekRole.ADMIN)) {
        const applicationCollection = mongo().collection<EntityData<JobApplication>>(JobApplication._collectionName);
        const applications = await applicationCollection
          .find({ userId: userID }, { projection: { jobPositionId: 1 } })
          .toArray();
        const jobPositionIDs = applications
          .map(application => application.jobPositionId)
          .filter((jobPositionID): jobPositionID is string => !!jobPositionID);
        const jobPositionCollection = mongo().collection<EntityData<JobPosition>>(JobPosition._collectionName);
        const jobPositions = await jobPositionCollection
          .find({ _id: { $in: jobPositionIDs } }, { projection: { companyID: 1 } })
          .toArray();
        if (
          !jobPositions.some(
            jobPosition =>
              jobPosition.companyID &&
              caller.hasPermission(MatchingWeekPermissions.MODIFY_COMPANY, jobPosition.companyID),
          )
        ) {
          throw new Error('Not authorized to view this user data');
        }
      }
    }
    const result = await mongo().withTransaction(async session => {
      // ensure user data exists
      const collection = mongo().collection(MWUserData._collectionName);
      await collection.updateOne(
        { userID },
        { $setOnInsert: { _class: MWUserData._class, _id: A.getGloballyUniqueID(), userID } },
        { upsert: true },
      );
      const result = await collection.findOne({ userID });
      return result;
    });
    return constructValue(result) as MWUserData;
  },
  params: [{ name: 'userID', type: () => ID }],
});

// Updates the logged-in user's first/last name. Called from the onboarding
// personal-info form so name changes propagate to mails and the applicant
// detail page. We can't just call `.save()` on the client-side User instance
// because it isn't an AdornisEntity (it's a projected query result).
//
// Security: the target userID is read from the server-side session context,
// NOT from a client parameter. A caller can only ever update their own name.
export const updateMyName = registerMutation({
  operationName: 'updateMyName',
  type: () => Boolean,
  params: [
    { name: 'firstName', type: () => String },
    { name: 'lastName', type: () => String },
  ],
  resolve: (firstName: string, lastName: string) => async () => {
    if (context.serverContext) throw new Error('updateMyName can only be called from a client session');
    const userID = context.userID as string | undefined;
    if (!userID) throw new NotLoggedInError();
    const trimmedFirst = (firstName ?? '').trim();
    const trimmedLast = (lastName ?? '').trim();
    if (!trimmedFirst || !trimmedLast) throw new Error('First and last name are required');
    const col = mongo().collection(User._collectionName);
    await col.updateOne({ _id: userID } as any, { $set: { firstName: trimmedFirst, lastName: trimmedLast } });
    return true;
  },
});

export const touchMyActivity = registerMutation({
  operationName: 'touchMyActivity',
  type: () => Boolean,
  params: [],
  resolve: () => async () => {
    if (context.serverContext) return false;
    const userID = context.userID as string | undefined;
    const userContext = context as typeof context & { impersonatingUser?: string };
    if (!userID || userContext.impersonatingUser) return false;

    const now = new Date();
    const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);
    const result = await mongo()
      .collection(User._collectionName)
      .updateOne(
        {
          _id: userID,
          $or: [{ lastActiveAt: { $exists: false } }, { lastActiveAt: null }, { lastActiveAt: { $lt: oneHourAgo } }],
        } as any,
        { $set: { lastActiveAt: now } },
      );
    return result.modifiedCount > 0;
  },
});

export const subscribeUserDataByUserID = registerSubscription({
  operationName: 'subscribeUserDataByUserID',
  type: () => MWUserData,
  resolve: (userID: string) => (gqlFields: BaseQLSelectionSet<MWUserData>) => {
    return from(getAndEnsureUserDataOfUserID(userID)({ _id: 1 })).pipe(
      switchMap(userData => watchQuery(MWUserData._collectionName, { userID })),
      map(userDatas => userDatas[0] as MWUserData),
    );
  },
  params: [{ name: 'userID', type: () => ID }],
});
